import pRetry from '@wix/cli-retry';
import type {
  DeviceCodeRequest,
  RevokeRefreshTokenRequest,
} from '@wix/ambassador-identity-oauth-v1-refresh-token/types';
import { getMyAccount } from '@wix/ambassador-identity-account-v2-account/http';
import {
  deviceCode as deviceCodeRequest,
  token as tokenRequest,
  revokeRefreshToken,
} from '@wix/ambassador-identity-oauth-v1-refresh-token/http';
import type { HttpClient, HttpError } from '@wix/cli-http-client';
import {
  isHttpError,
  isHttpApplicationError,
  createHttpClient,
} from '@wix/cli-http-client';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { fields, payload, variant } from 'variant';
import type { SiteAuth, UserInfo } from './schemas.js';
import {
  deviceCodeSchema,
  tokenSchema,
  userInfoSchema,
  getMyAccountSchema,
} from './schemas.js';

const {
  InvalidResponseData,
  FailedToGetAuthToken,
  FailedToGetDeviceCode,
  FailedToRenewAuthToken,
  FailedToRenewAuthTokenSiteNotFound,
  FailedToRevokeRefreshToken,
} = CliErrorCode;

const clientId = '6f95cec8-3e98-48b9-b4e5-1fb92fcd9973';

const getCurrentTimestamp = () => Math.floor(Date.now() / 1000);

interface TokenRequest {
  clientId: string;
  grantType: string;
  scope?: string;
  deviceCode?: string;
  refreshToken?: string;
  siteId?: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export class DeviceCodeNotYetVerifiedError extends Error {}

export const RenewTokenData = variant({
  TokenRenewed: payload<SiteAuth>(),
  RefreshTokenRevoked: fields<{ cause: HttpError }>(),
});

class AuthClient {
  constructor(private readonly httpClient: HttpClient) {}

  async requestDeviceCode(req: DeviceCodeRequest = {}) {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(deviceCodeRequest({ ...req, clientId })),
        { retries: 3 }
      );

      return deviceCodeSchema.parse(data);
    } catch (error) {
      throw new CliError({
        code: FailedToGetDeviceCode(),
        cause: error,
      });
    }
  }

  async createToken(req: { deviceCode: string }) {
    try {
      const payload: TokenRequest = {
        clientId,
        grantType: 'urn:ietf:params:oauth:grant-type:device_code',
        scope: 'offline_access',
        ...req,
      };

      const tokenData = await pRetry(
        async () => {
          // any is needed because `tokenRequest` uses `RawHttpRequest` and `RawHttpResponse` which are not typed correctly
          const { data, requestId } =
            await this.httpClient.request<TokenResponse>(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              tokenRequest(payload as any) as any
            );

          const result = tokenSchema.safeParse(data);

          if (result.success) {
            return result.data;
          }

          throw new CliError({
            code: InvalidResponseData(),
            cause: result.error,
            info: { requestId, data },
          });
        },
        { retries: 3 }
      );

      return {
        ...tokenData,
        issuedAt: getCurrentTimestamp(),
      };
    } catch (error) {
      if (
        isHttpError(error) &&
        (error.response?.data?.message === 'Device code is not yet verified' ||
          // https://github.com/wix-private/wix-cli/pull/655#discussion_r1145992702
          error.response?.data?.error === 'authorization_pending')
      ) {
        throw new DeviceCodeNotYetVerifiedError(
          'Device code is not yet verified.'
        );
      }

      throw new CliError({
        code: FailedToGetAuthToken(),
        cause: error,
      });
    }
  }

  async renewToken(req: { refreshToken: string; siteId?: string }) {
    try {
      const payload: TokenRequest = {
        clientId,
        grantType: 'refresh_token',
        ...req,
      };

      // any is needed because `tokenRequest` uses `RawHttpRequest` and `RawHttpResponse` which are not typed correctly
      const { data } = await pRetry(
        () =>
          this.httpClient.request<TokenResponse>(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            tokenRequest(payload as any) as any
          ),
        { retries: 3 }
      );

      const tokenData = tokenSchema.parse(data);

      return RenewTokenData.TokenRenewed({
        ...tokenData,
        issuedAt: getCurrentTimestamp(),
      });
    } catch (error) {
      if (isHttpError(error)) {
        if (
          error.response?.status === 400 &&
          error.response.data?.error === 'invalid_grant'
        ) {
          return RenewTokenData.RefreshTokenRevoked({ cause: error });
        }
        if (
          req.siteId &&
          error.response?.status === 404 &&
          isHttpApplicationError(error) &&
          error.response.data.details.applicationError.code === 'SITE_NOT_FOUND'
        ) {
          throw new CliError({
            code: FailedToRenewAuthTokenSiteNotFound({ siteId: req.siteId }),
            cause: error,
          });
        }
      }

      throw new CliError({
        code: FailedToRenewAuthToken(),
        cause: error,
      });
    }
  }

  async revokeRefreshToken(
    req: RevokeRefreshTokenRequest,
    { email }: UserInfo
  ) {
    try {
      const { data } = await this.httpClient.request(revokeRefreshToken(req));

      return data;
    } catch (error) {
      throw new CliError({
        code: FailedToRevokeRefreshToken({
          email,
        }),
        info: {
          email,
        },
        cause: error,
      });
    }
  }

  async userInfo({ accessToken }: { accessToken: string }) {
    try {
      const httpClient = createHttpClient({
        type: 'backoffice',
        getAppToken: async () => accessToken,
      });
      const { data: userInfoData } = await httpClient.get(
        '/_serverless/wix-cli-userinfo/userinfo'
      );

      return userInfoSchema.parse(userInfoData);
    } catch (error) {
      throw new CliError({
        code: CliErrorCode.FailedToGetUserInfo(),
        cause: error,
      });
    }
  }

  async getMyAccount({ token }: { token: string }) {
    try {
      const httpClient = createHttpClient({
        type: 'backoffice',
        getAppToken: async () => token,
      });
      const { data } = await httpClient.request(getMyAccount({}));

      return getMyAccountSchema.parse(data).account;
    } catch (error) {
      // 48876 is code for `TargetAccountIsMissingErrorCode`,
      // which in this case means that user tried to log in with invalid API key.
      if (
        isHttpError(error) &&
        error.response?.status === 428 &&
        isHttpApplicationError(error) &&
        error.response.data.details.applicationError.code === '48876'
      ) {
        throw new CliError({
          code: CliErrorCode.InvalidApiKey(),
          cause: error,
        });
      }
      if (
        isHttpError(error) &&
        error.response?.status === 404 &&
        isHttpApplicationError(error) &&
        error.response.data.details.applicationError.code === '48886'
      ) {
        throw new CliError({
          code: CliErrorCode.AccountWasDeleted(),
          cause: error,
        });
      }
      throw new CliError({
        code: CliErrorCode.FailedToGetMyAccount(),
        cause: error,
      });
    }
  }
}

export const client = new AuthClient(createHttpClient({ type: 'backoffice' }));
